/**
 * Renaming and deleting a space, without a modal (RFC 0281 S5a).
 *
 * ONE COPY OF THE BEHAVIOUR, TWO PLACES IT IS DRAWN. Spaces render as sidebar
 * rows and, in tabs mode, along the top. Both offer the same four affordances —
 * switch, double click to rename, × then tick to delete, + to create — and both
 * were written with their own `renaming` / `draft` / `confirming` state and
 * their own handlers. That is one interaction implemented twice, and the second
 * copy only matched the first because it was written by reading it: the moment
 * either grows a fifth affordance they drift, and the drift shows up as a view
 * that quietly does less, which is precisely what the tabs mode was not
 * supposed to be.
 *
 * So the state and the transitions live here and the components are left as two
 * renderings of one thing. What stays in each is layout, which is the part that
 * genuinely differs.
 *
 * INLINE, NO `window.confirm`, no modal — the convention this app keeps
 * everywhere else (`SessionRow` says so outright).
 */

import { useCallback, useState } from 'react'

export interface SpaceActions {
  /** The space being renamed, or null. */
  renaming: string | null
  /** The live text of the rename input. */
  draft: string
  /** The space whose delete is awaiting its confirming tick, or null. */
  confirming: string | null
  setDraft: (value: string) => void
  /** Start renaming, seeded with the name it already has. */
  beginRename: (spaceId: string, currentName: string) => void
  /**
   * Finish a rename.
   *
   * A BLANK NAME IS DISCARDED rather than written. Committing on blur means an
   * empty input is one stray click away, and a nameless row is unclickable in
   * the tabs strip — there would be nothing left to hit to fix it.
   */
  commitRename: (spaceId: string) => void
  cancelRename: () => void
  /** First × press: arm the confirm. */
  askDelete: (spaceId: string) => void
  /** Second press: actually delete, and disarm. */
  confirmDelete: (spaceId: string) => void
  cancelDelete: () => void
}

export function useSpaceActions({
  onRename,
  onDelete,
}: {
  onRename?: (spaceId: string, name: string) => void
  onDelete?: (spaceId: string) => void
}): SpaceActions {
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [initial, setInitial] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)

  const beginRename = useCallback((spaceId: string, currentName: string) => {
    setRenaming(spaceId)
    setDraft(currentName)
    setInitial(currentName)
    // Arming a rename disarms a pending delete: the two controls sit next to
    // each other, and a × left armed under an open rename input is a delete
    // waiting to happen on the next click.
    setConfirming(null)
  }, [])

  const commitRename = useCallback(
    (spaceId: string) => {
      // ESCAPE MUST BEAT BLUR. Cancelling clears `renaming`, and the input then
      // unmounts — which fires `onBlur`, whose handler would commit the very
      // edit that was just abandoned. Guarding on the id makes commit
      // idempotent and makes cancel actually cancel.
      if (renaming !== spaceId) return
      const name = draft.trim()
      // AN UNCHANGED NAME IS NOT WRITTEN. The input is seeded with the label,
      // and an unnamed space's label is derived: writing it back on a stray
      // double click and blur would pin it, and it would stop following the
      // space's contents without anyone choosing that.
      if (name && name !== initial.trim()) onRename?.(spaceId, name)
      setRenaming(null)
      setDraft('')
    },
    [draft, initial, onRename, renaming],
  )

  const cancelRename = useCallback(() => {
    setRenaming(null)
    setDraft('')
  }, [])

  const askDelete = useCallback((spaceId: string) => setConfirming(spaceId), [])

  const confirmDelete = useCallback(
    (spaceId: string) => {
      onDelete?.(spaceId)
      setConfirming(null)
    },
    [onDelete],
  )

  const cancelDelete = useCallback(() => setConfirming(null), [])

  return {
    renaming,
    draft,
    confirming,
    setDraft,
    beginRename,
    commitRename,
    cancelRename,
    askDelete,
    confirmDelete,
    cancelDelete,
  }
}
